import React, { useState, useContext, useEffect, useRef } from 'react';
import './Route.scss';
import VideoSimple from '@/v2/components/media/video/VideoSimple';
import TimelineText from '@/v2/components/atoms/TimelineText';
import TimeController from '@/v2/components/controllers/TimeController';
import { AnalyticsContext } from '@/v2/components/application/Analytics';
import Dialog from '@/v2/components/layouts/Dialog';
import Warning from '@/v2/components/atoms/Icon/Warning';
import NissanConnect from '@/v2/components/atoms/Icon/NissanConnect';
import ChargingPoint from '@/v2/components/atoms/Icon/ChargingPoint';
import VisibilityTimer from '@/v2/components/behaviours/VisibilityTimer';
import Circle from '@/v2/components/atoms/Circle';
import RouteLine from '@/v2/components/atoms/RouteLine';
import SvgController from '@/v2/components/controllers/SvgController';
import Header from '@/v2/components/application/Header';
import Disclaimer from '@/v2/components/layouts/Disclaimer';
import { ConnectivityContext } from '@/v2/state/providers';
import { MomentStateContext } from '@/v2/state/momentState';
import { IsIE11 } from '@/v2/components/utils/DeviceUtils/DeviceUtils';
import { timeout, saveTimeout, clearTimeouts } from '@/v2/components/utils/Timeout';

const STATE = {
    intro: 'route',
    tooltip: 'tooltip',
    cta: 'cta',
    chargingPoints: 'chargingPoints',
    transitionNext: 'transitionNext',
};

let videoController = null;
let timeController = null;

const videoReady = (controller, onReady) => {
    videoController = controller;

    onReady();
};

const play = (timeline) => {
    videoController.Play();
    timeController.AddTimeoutFromTimeline(timeline, () => {});
};

const activate = (setTime, setState, timeline) => {
    timeController = new TimeController(setTime);
    timeController.AddTimeoutFromTimeline(timeline, () => {
        setState(STATE.cta);
    });
};

const resizeHandler = (svgRef, fastestRouteRef, recommendedPointRef) => {
    if (svgRef.current) {
        const svgController = new SvgController(svgRef.current.firstChild);
        svgController.setOverlay(fastestRouteRef.current, 75);
        svgController.setOverlay(recommendedPointRef.current, 50);
        svgController.positionOverlays();
    }
};

export default ({ isActive, data, onFinished, onReady }) => {
    const [state, setState] = useState(STATE.intro);
    const [showLine, setShowLine] = useState(true);
    const { trackEvent } = useContext(AnalyticsContext);
    const [{ disclaimer }] = useContext(ConnectivityContext);
    const { dispatch: momentDispatch } = useContext(MomentStateContext);

    const svgRef = useRef();
    const fastestRouteRef = useRef();
    const recommendedPointRef = useRef();
    const time = useRef(TimelineText.Time());

    const fastestRouteTooltip = data?.tooltip.find((item) => item.name === 'fastestRoute');
    const recommendedPoint = data?.tooltip.find((item) => item.name === 'recommendedPoint');

    const videoFinished = () => {
        // delaying execution till video playback finishes
        timeout(onFinished, 800);
    };

    const chargingPoints = () => {
        saveTimeout(
            timeController.AddTimeoutFromTimeline(data.text.secondaryTimeline, () => {
                setShowLine(false);
                setTimeout(() => {
                    play(data.text.secondaryTimeline);
                    setState(STATE.transitionNext);
                }, 1200);
            })
        );
    };

    const fillLineTo = (offset) => {
        const line = document.getElementById('routeLinePath');

        if (offset <= 0 || !line) {
            return;
        }

        line.style.strokeDashoffset = offset - 16;

        requestAnimationFrame(() => fillLineTo(offset - 16));
    };

    useEffect(() => () => clearTimeouts(), []);

    useEffect(() => {
        if (isActive) {
            activate(time.current.Set, setState, data.text.timeline);
            resizeHandler(svgRef, fastestRouteRef, recommendedPointRef);
            window.addEventListener('resize', resizeHandler.bind(this, svgRef, fastestRouteRef, recommendedPointRef));

            if (IsIE11()) {
                fillLineTo(2000);
            }
        } else {
            window.removeEventListener('resize', resizeHandler.bind(this, svgRef, fastestRouteRef, recommendedPointRef));
        }
    }, [isActive]);

    useEffect(() => {
        if (isActive && videoController) {
            videoController.ProgressDispatch(momentDispatch);
        }
    }, [isActive, videoController]);

    const options = {
        bufferBeforePlay: true,
        onReady: (controller) => videoReady(controller, onReady),
        autoPlay: false,
        onFinished: () => videoFinished(onFinished),
    };

    const handleCta = () => {
        setState(STATE.chargingPoints);
        chargingPoints();
        trackEvent('vcClick', { vcStep: 'trip', vcClick: `connectivity:${data.dialogbox[0].label.cta.toLowerCase()}` });
    };

    const disclaimerContent = isActive && disclaimer ? <Disclaimer data={disclaimer} dark /> : '';
    const timeline = state === STATE.intro ? data.text.timeline : data.text.secondaryTimeline;
    return (
        <div className="Route">
            <div className="Route__middle" />
            <VideoSimple offsetRatio={1.9} offset={100} src={data.video} options={options} />
            <Header show={isActive} dark={state !== STATE.cta} hideLogo />
            {isActive && state !== STATE.transitionNext && (
                <>
                    <TimelineText data={timeline} time={time.current} background="light" color="black" key={`timeline-${state}`} />
                    <div className="Route__line-container">
                        <div className={`Route__line Route__line--${showLine ? 'enter' : 'leave'}`} ref={svgRef}>
                            <RouteLine />
                        </div>
                    </div>
                    <div className="Route__tooltip Route__tooltip--fastest" ref={fastestRouteRef}>
                        <VisibilityTimer
                            activate={isActive}
                            start={parseInt(fastestRouteTooltip.duration.split('-')[0], 10)}
                            end={parseInt(fastestRouteTooltip.duration.split('-')[1], 10)}
                        >
                            <Dialog
                                bodyText={fastestRouteTooltip.label.body}
                                bodyTextHeader={fastestRouteTooltip.label.title}
                                footerText={fastestRouteTooltip.label.warningText}
                                footerTextIcon={<Warning />}
                                footerTextIconBlink
                                variant="bubble"
                                className="small"
                            />
                        </VisibilityTimer>
                    </div>
                    <div className="Route__tooltip Route__tooltip--icon" ref={recommendedPointRef}>
                        <VisibilityTimer
                            activate={state === STATE.chargingPoints}
                            start={parseInt(recommendedPoint.duration.split('-')[0], 10)}
                            end={parseInt(recommendedPoint.duration.split('-')[1], 10)}
                        >
                            <Dialog
                                bodyText={recommendedPoint.label.body}
                                bodyTextHeader={recommendedPoint.label.title}
                                footerText={recommendedPoint.label.warningText}
                                variant="bubble"
                            />
                            <Circle>
                                <ChargingPoint />
                            </Circle>
                        </VisibilityTimer>
                    </div>

                    {state === STATE.cta && (
                        <div className="Route__dialogbox">
                            <Dialog
                                show={state === STATE.cta}
                                bodyText={data.dialogbox[0].label.body}
                                bodyTextHeader={data.dialogbox[0].label.title}
                                buttonText={data.dialogbox[0].label.cta}
                                headerRightText={data.dialogbox[0].label.headingRight}
                                headerTitle={data.dialogbox[0].label.headingLeft}
                                headerTitleIcon={<NissanConnect />}
                                onButtonClick={handleCta}
                            />
                        </div>
                    )}

                    <div className={`Route__dialogbox-mask Route__dialogbox-mask--${state === STATE.cta ? 'enter' : 'leave'}`} />
                </>
            )}
            {disclaimerContent}
        </div>
    );
};
